import React from 'react';
import { paletteValue, resolveSx, splitSystemProps } from './style-utils.js';
import './palette.less';
import './text.less';

// Typography primitive replacing @mui/material Typography for the common
// cases. Variant metrics in text.less use the same --ls-* typography tokens
// the apps' MUI theme mapped variants to, so the swap is visually neutral.
const VARIANT_ELEMENTS: Record<string, string> = {
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
  h5: 'h5',
  h6: 'h6',
  subtitle1: 'h6',
  subtitle2: 'h6',
  body1: 'p',
  body2: 'p',
  caption: 'span',
  overline: 'span',
};

// Legacy MUI color prop shorthands.
const LEGACY_COLOR_TOKENS: Record<string, string> = {
  textPrimary: 'text.primary',
  textSecondary: 'text.secondary',
  textDisabled: 'text.disabled',
};

export type TextVariant =
  | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
  | 'subtitle1' | 'subtitle2'
  | 'body1' | 'body2'
  | 'caption' | 'overline';

export interface TextProps extends Omit<React.HTMLAttributes<HTMLElement>, 'color'> {
  variant?: TextVariant | (string & {});
  as?: React.ElementType;
  component?: React.ElementType;
  gutterBottom?: boolean;
  paragraph?: boolean;
  noWrap?: boolean;
  align?: React.CSSProperties['textAlign'];
  /** Palette token, legacy MUI color shorthand, or raw CSS color. */
  color?: string;
  sx?: React.CSSProperties | Record<string, unknown>;
}

export const Text = React.forwardRef<HTMLElement, TextProps>(({
  variant = 'body1',
  as,
  component,
  gutterBottom = false,
  paragraph = false,
  noWrap = false,
  align,
  color,
  sx,
  style,
  className,
  children,
  ...props
}, ref) => {
  const Element = (component || as || (paragraph ? 'p' : VARIANT_ELEMENTS[variant]) || 'p') as React.ElementType;
  const resolvedColor = color
    ? (paletteValue(LEGACY_COLOR_TOKENS[color] || color) || color)
    : undefined;
  const { system, rest } = splitSystemProps(props);
  const resolved = resolveSx(system, sx);
  const isTextSecondary = color === 'textSecondary' || color === 'text.secondary';
  const resolvedClassName = [
    'ls-text',
    VARIANT_ELEMENTS[variant] ? `ls-text--${variant}` : 'ls-text--body1',
    // Legacy MUI class names so existing app CSS targeting .MuiTypography-*
    // keeps applying during the migration; remove once those selectors are gone.
    'MuiTypography-root',
    VARIANT_ELEMENTS[variant] ? `MuiTypography-${variant}` : '',
    isTextSecondary ? 'MuiTypography-colorTextSecondary' : '',
    gutterBottom ? 'ls-text--gutter-bottom' : '',
    paragraph ? 'ls-text--paragraph' : '',
    noWrap ? 'ls-text--no-wrap' : '',
    resolved.className || '',
    className || '',
  ].filter(Boolean).join(' ');
  const resolvedStyle = {
    ...(align ? { textAlign: align } : null),
    ...(resolvedColor ? { color: resolvedColor } : null),
    ...resolved.style,
    ...style,
  };

  return (
    <Element
      ref={ref}
      className={resolvedClassName}
      style={Object.keys(resolvedStyle).length ? resolvedStyle : undefined}
      {...rest}
    >
      {children}
    </Element>
  );
});

Text.displayName = 'Text';

// MUI DialogContentText compat: body text in the secondary colour.
export const DialogContentText = React.forwardRef<HTMLElement, TextProps>((props, ref) => (
  <Text ref={ref} variant="body1" color="text.secondary" {...props} />
));
DialogContentText.displayName = 'DialogContentText';

export default Text;
